import React, { useEffect, useState, useMemo } from 'react';
import styles from './AboutPage.module.css';
import { MarkdownDisplay } from '@schamburg-web/components';

// Configuration for external about content directory
// To use an external directory:
// 1. Set useExternalDirectory to true
// 2. Set externalBaseUrl to your external directory path (e.g., '/about-content')
// 3. Place your markdown file with the name specified in markdownFileName
// 4. Place images in the subdirectory specified in imagesSubdir
// 5. In markdown, reference images using 'images/' prefix (e.g., 'images/image.png')
//    These will be automatically transformed to '/about-content/images/image.png'
const ABOUT_CONTENT_CONFIG = {
  useExternalDirectory: true, // Set to false to use local public/about.md
  externalBaseUrl: '/about-content', // External directory URL
  markdownFileName: 'about.md', // Name of the markdown file
  imagesSubdir: '', // Subdirectory for images within about-content
};

const AboutContent: React.FC = () => {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFile, setCurrentFile] = useState<string>(
    ABOUT_CONTENT_CONFIG.markdownFileName
  );

  // Handle markdown content changes from MarkdownDisplay component
  const handleMarkdownChange = (newMarkdown: string, filename?: string) => {
    setMarkdown(newMarkdown);
    if (filename) {
      setCurrentFile(filename);
    }
    // Clear any existing errors
    setError(null);
  };

  // Memoize the markdown URL based on current file
  const markdownUrl = useMemo(() => {
    if (ABOUT_CONTENT_CONFIG.useExternalDirectory) {
      return `${ABOUT_CONTENT_CONFIG.externalBaseUrl}/${currentFile}`;
    } else {
      // Fallback to local file
      return `/${currentFile}`;
    }
  }, [currentFile]);

  useEffect(() => {
    fetch(markdownUrl)
      .then((res) => {
        if (!res.ok)
          throw new Error(`Failed to fetch content from ${markdownUrl}`);
        return res.text();
      })
      .then((text) => {
        // If it looks like HTML (e.g. from index.html), fall back
        if (text.startsWith('<!DOCTYPE html>') || text.includes('<html')) {
          console.warn('Received HTML instead of markdown — using fallback');
          setMarkdown('');
          return;
        }
        setMarkdown(text);
      })
      .catch((err) => {
        console.error('Failed to load markdown:', err);
        setError(`Failed to load content from ${markdownUrl}`);
        setMarkdown('# Error\nContent could not be loaded.');
      })
      .finally(() => setLoading(false));
  }, [markdownUrl]);

  // Handle loading and error states
  if (loading)
    return (
      <div className={styles.markdownContainer} style={{ padding: '16px' }}>
        Loading...
      </div>
    );
  if (error)
    return (
      <div className={styles.markdownContainer} style={{ padding: '16px' }}>
        Error: {error}
      </div>
    );

  return (
    <MarkdownDisplay
      markdown={markdown}
      styles={{
        markdownContainer: styles.markdownContainer,
        aboutLinks: styles.aboutLinks,
        defaultImage: styles.defaultImage,
        smallImage: styles.smallImage,
        mainImage: styles.mainImage,
        markdownTable: styles.markdownTable,
        markdownTableCell: styles.markdownTableCell,
        markdownInnerContainer: styles.markdownInnerContainer,
      }}
      contentConfig={{
        useExternalDirectory: ABOUT_CONTENT_CONFIG.useExternalDirectory,
        externalBaseUrl: ABOUT_CONTENT_CONFIG.externalBaseUrl,
        imagesSubdir: ABOUT_CONTENT_CONFIG.imagesSubdir,
      }}
      onMarkdownChange={handleMarkdownChange}
    />
  );
};

export default AboutContent;
